/**
 * Where the focus is in the draft, and how the page puts it on a row (ease round, 13 Oct
 * 2026: the keys of the case page had no path through the rows).
 *
 * The table is a DS grid operated with the keys (`interaction="grid"`): the arrow keys
 * walk its cells and every cell carries the key of its row. The cards (a phone, the
 * Outlook pane) are list items that carry the line id. `currentDraftLineId` reads the row
 * that holds the focus from either; the row keys (Enter, `n`, `p`, `q`, `i`) act on it.
 *
 * `focusDraftRow` is what a click on the sentence next to the big button does: the row
 * scrolls into view, takes the focus and is marked for two seconds (`data-row-highlight`,
 * a token background set by the table and the list, no inline style).
 *
 * DOM only: no React, no requests. Every function is a no-op without a document.
 */

/** The DS grid marks every cell with the key of its row (`getRowId`: the line id). */
export const GRID_ROW_ATTR = "data-norican-grid-row";
/** The DS grid marks every cell that takes part in the arrow keys. */
export const GRID_CELL_ATTR = "data-norican-grid-cell";
/** The DS grid moves Tab between the controls of one cell that carry this mark. */
export const GRID_WIDGET_ATTR = "data-norican-grid-widget";
/** The id of the line: on the status badge in the table, on the card's `<li>` in the list. */
export const DRAFT_LINE_ATTR = "data-draft-line";
/** On the row while it is marked after a click on a blocker sentence. */
export const ROW_HIGHLIGHT_ATTR = "data-row-highlight";
/** How long a row stays marked. */
export const ROW_HIGHLIGHT_MS = 2_000;

/** The row of a line: the `<tr>` in the table, the card's `<li>` in the list; null when not shown. */
export function draftRowElement(lineId: string, root?: ParentNode): HTMLElement | null {
  const scope = root ?? (typeof document === "undefined" ? null : document);
  if (!scope) {
    return null;
  }
  const mark = scope.querySelector<HTMLElement>(`[${DRAFT_LINE_ATTR}="${CSS.escape(lineId)}"]`);
  return mark?.closest<HTMLElement>("tr, li") ?? mark ?? null;
}

/**
 * Put the focus on the row of a line and scroll it into view. In the table the focus goes
 * to the first cell of the row, so the arrow keys go on from there. With `highlight` the
 * row is marked for two seconds. Returns false when the row is not on the page.
 */
export function focusDraftRow(
  lineId: string,
  options: { highlight?: boolean; scroll?: boolean } = {},
): boolean {
  const row = draftRowElement(lineId);
  if (!row) {
    return false;
  }
  if (options.scroll !== false) {
    row.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  const cell = row.querySelector<HTMLElement>(`td[${GRID_CELL_ATTR}]`);
  const holder = cell ?? row;
  if (!holder.hasAttribute("tabindex")) {
    holder.setAttribute("tabindex", "-1");
  }
  holder.focus({ preventScroll: true });
  if (options.highlight) {
    row.setAttribute(ROW_HIGHLIGHT_ATTR, "");
    window.setTimeout(() => row.removeAttribute(ROW_HIGHLIGHT_ATTR), ROW_HIGHLIGHT_MS);
  }
  return true;
}

/**
 * Give the focus back to a row after a dialog that a row key opened has closed. A dialog
 * returns the focus to the button that opened it; a key has no button, so the focus was
 * left on the page body and the next key had no row. The row takes it only when nothing
 * else did (twice: once at once, once after the closing animation).
 */
export function returnFocusToRow(lineId: string | null): void {
  if (!lineId || typeof window === "undefined") {
    return;
  }
  const giveBack = () => {
    const active = document.activeElement;
    if (!active || active === document.body) {
      focusDraftRow(lineId, { scroll: false });
    }
  };
  window.setTimeout(giveBack, 50);
  window.setTimeout(giveBack, 400);
}

/**
 * After a key decided a row (Enter confirmed it, `n` said "No item"), the focus goes to
 * the next row that needs the person, so the same key works down the list (review of the
 * ease round, 13 Oct 2026: five Enter presses confirmed one row of five, because the
 * focus stayed on the row that moved to "Matched"). The decided row moves in the page a
 * moment later, and a row that moves can drop the focus: the next row takes it at once
 * and again after the move, unless the person put the focus somewhere else in between.
 */
export function focusNextRowAfterDecision(nextLineId: string, decidedLineId: string): void {
  if (typeof window === "undefined") {
    return;
  }
  focusDraftRow(nextLineId, { scroll: false });
  const again = () => {
    const active = document.activeElement;
    const row = currentDraftLineId(active);
    if (!active || active === document.body || row === decidedLineId) {
      focusDraftRow(nextLineId, { scroll: false });
    }
  };
  window.setTimeout(again, 60);
  window.setTimeout(again, 400);
}

/** The id of the first row of the draft as the page lists it ("Needs you" comes first). */
export function firstDraftLineId(root?: ParentNode): string | null {
  const scope = root ?? (typeof document === "undefined" ? null : document);
  const mark = scope?.querySelector<HTMLElement>(`[data-draft-lines] [${DRAFT_LINE_ATTR}]`);
  return mark?.getAttribute(DRAFT_LINE_ATTR) ?? null;
}

/**
 * The line whose row holds the focus, or null when the focus is not in a row of the
 * draft. A group row of the table ("Needs you") is not a line.
 */
export function currentDraftLineId(active?: Element | null): string | null {
  const element = active ?? (typeof document === "undefined" ? null : document.activeElement);
  if (!element || typeof element.closest !== "function") {
    return null;
  }
  const cell = element.closest(`[${GRID_ROW_ATTR}]`);
  if (cell) {
    const key = cell.getAttribute(GRID_ROW_ATTR) ?? "";
    return key !== "" && !key.startsWith("group:") ? key : null;
  }
  const card = element.closest(`li[${DRAFT_LINE_ATTR}]`);
  if (card) {
    return card.getAttribute(DRAFT_LINE_ATTR);
  }
  // The details of a row, opened under it.
  const details = element.closest("[data-line-detail-row]");
  if (details) {
    return details.getAttribute("data-line-detail-row");
  }
  return (
    element.closest("tr")?.querySelector(`[${DRAFT_LINE_ATTR}]`)?.getAttribute(DRAFT_LINE_ATTR) ??
    null
  );
}

/**
 * True when a key press is on a row itself (a cell of the table, a card), not on a
 * control inside it: Enter on a button still presses that button.
 */
export function isRowTarget(target: EventTarget | null): boolean {
  const element = target as Element | null;
  if (!element || typeof element.matches !== "function") {
    return false;
  }
  return element.matches(`td[${GRID_CELL_ATTR}], li[${DRAFT_LINE_ATTR}]`);
}
